import {
  projectUnsponsoredFamilyRuntimeEvidence,
  type UnsponsoredFamilyEvidenceStatus,
} from './UnsponsoredFamilyRuntimeEvidenceProjectionEngine';

export type UnsponsoredFamilyCompletionDecision =
  | 'COMPLETE'
  | 'BLOCKED'
  | 'UNVERIFIED';

export interface UnsponsoredFamilyCompletionCheck {
  id:
    | 'policy-contract'
    | 'database-enforcement'
    | 'ui-runtime-alignment'
    | 'runtime-evidence-projection'
    | 'family-identity-preserved'
    | 'own-profile-authority'
    | 'five-photo-limit'
    | 'paid-capability-restrictions'
    | 'claimed-ownership-boundaries'
    | 'membership-upgrade-preserves-binding';
  title: string;
  required: true;
  status: UnsponsoredFamilyEvidenceStatus;
  explanation: string;
}

export interface UnsponsoredFamilyCompletionGate {
  title: string;
  subsystem: 'membership';
  authorityDomain: 'unsponsored-family-access';
  decision: UnsponsoredFamilyCompletionDecision;
  status: UnsponsoredFamilyEvidenceStatus;
  completionAllowed: boolean;

  requiredCheckCount: number;
  passedCheckCount: number;
  failedCheckCount: number;
  unverifiedCheckCount: number;

  failedChecks: UnsponsoredFamilyCompletionCheck[];
  unverifiedChecks: UnsponsoredFamilyCompletionCheck[];
  checks: UnsponsoredFamilyCompletionCheck[];

  constitutionalRule: string;
  nextMilestone: string;
  evaluatedAt: string;
}

export function evaluateUnsponsoredFamilyCompletionGate():
  UnsponsoredFamilyCompletionGate {
  const evidence =
    projectUnsponsoredFamilyRuntimeEvidence();

  const milestoneStatus = (
    milestoneId: 'MAE-003A' | 'MAE-003B' | 'MAE-003C',
  ): UnsponsoredFamilyEvidenceStatus =>
    evidence.milestones.find(
      milestone => milestone.milestone === milestoneId,
    )?.status ?? 'UNVERIFIED';

  const checks: UnsponsoredFamilyCompletionCheck[] = [
    {
      id: 'policy-contract',
      title: 'Unsponsored family access policy contract',
      required: true,
      status: milestoneStatus('MAE-003A'),
      explanation:
        'Permanent family identity and limited unsponsored capabilities must be formally defined.',
    },
    {
      id: 'database-enforcement',
      title: 'Live database capability enforcement',
      required: true,
      status: milestoneStatus('MAE-003B'),
      explanation:
        'Profile ownership, media ownership, photo limits, video denial, and call denial must be database-enforced.',
    },
    {
      id: 'ui-runtime-alignment',
      title: 'UI and runtime capability alignment',
      required: true,
      status: milestoneStatus('MAE-003C'),
      explanation:
        'The application must recognize both self-paid and sponsored capability without granting unsupported access.',
    },
    {
      id: 'runtime-evidence-projection',
      title: 'Membership Governor evidence projection',
      required: true,
      status: evidence.status,
      explanation:
        'The complete unsponsored-family authority chain must be visible in the Command Center.',
    },
    {
      id: 'family-identity-preserved',
      title: 'Permanent family identity',
      required: true,
      status:
        evidence.familyIdentityPermanent &&
        !evidence.membershipRequiredForFamilyIdentity
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Family-tree membership must remain independent from paid capability.',
    },
    {
      id: 'own-profile-authority',
      title: 'Own-profile authority',
      required: true,
      status:
        evidence.mayJoinFamilyTree &&
        evidence.mayClaimOwnProfile &&
        evidence.mayEditOwnProfile
          ? 'PASS'
          : 'FAIL',
      explanation:
        'An unsponsored relative must be able to join, claim, and maintain their own profile.',
    },
    {
      id: 'five-photo-limit',
      title: 'Five-photo limit including profile image',
      required: true,
      status:
        evidence.photoLimit === 5 &&
        evidence.profileImageCountsTowardLimit
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Unsponsored photo authority must be capped at five total images including the profile image.',
    },
    {
      id: 'paid-capability-restrictions',
      title: 'Paid capability restrictions',
      required: true,
      status:
        evidence.unsponsoredVideoDenied &&
        evidence.unsponsoredCallsDenied &&
        evidence.premiumArchiveDenied
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Video, calls, and premium archive must require paid or sponsored capability.',
    },
    {
      id: 'claimed-ownership-boundaries',
      title: 'Claimed profile and media ownership',
      required: true,
      status:
        evidence.claimedProfileOwnershipEnforced &&
        evidence.claimedMediaOwnershipEnforced &&
        evidence.databaseCapabilityBoundaryActive
          ? 'PASS'
          : 'FAIL',
      explanation:
        'No member may modify another claimed member’s profile or media.',
    },
    {
      id: 'membership-upgrade-preserves-binding',
      title: 'Membership upgrade preserves family binding',
      required: true,
      status:
        evidence.sponsoredMemberMayPurchaseOwnPlan &&
        evidence.membershipUpgradePreservesFamilyBinding
          ? 'PASS'
          : 'FAIL',
      explanation:
        'A sponsored or unsponsored relative may buy a separate plan without changing family identity.',
    },
  ];

  const passedCheckCount = checks.filter(
    check => check.status === 'PASS',
  ).length;

  const failedChecks = checks.filter(
    check => check.status === 'FAIL',
  );

  const unverifiedChecks = checks.filter(
    check => check.status === 'UNVERIFIED',
  );

  const completionAllowed =
    passedCheckCount === checks.length &&
    failedChecks.length === 0 &&
    unverifiedChecks.length === 0;

  const decision: UnsponsoredFamilyCompletionDecision =
    completionAllowed
      ? 'COMPLETE'
      : failedChecks.length > 0
        ? 'BLOCKED'
        : 'UNVERIFIED';

  const status: UnsponsoredFamilyEvidenceStatus =
    decision === 'COMPLETE'
      ? 'PASS'
      : decision === 'BLOCKED'
        ? 'FAIL'
        : 'UNVERIFIED';

  return Object.freeze({
    title: 'Unsponsored Family Access Completion Gate',
    subsystem: 'membership',
    authorityDomain: 'unsponsored-family-access',
    decision,
    status,
    completionAllowed,

    requiredCheckCount: checks.length,
    passedCheckCount,
    failedCheckCount: failedChecks.length,
    unverifiedCheckCount: unverifiedChecks.length,

    failedChecks,
    unverifiedChecks,
    checks,

    constitutionalRule:
      'Family identity is permanent. Capabilities are licensed. Resources are governed.',
    nextMilestone:
      'MAE-004 — Membership Counters and Dashboard Authority',
    evaluatedAt: new Date().toISOString(),
  });
}

export const UNSPONSORED_FAMILY_COMPLETION_GATE_READY =
  true;
